// src/components/context/AuthContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { API_URL } from "../config/api";

export const STORAGE_KEY = "auth.session.v1";      // 사용자 세션 캐시

/* =========================
 *  Auth Context
 * ========================= */
const AuthContext = createContext(null);

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);   // { id, tenant_id, name, role }
    const [loading, setLoading] = useState(true);

    /* 1) 첫 렌더: 로컬스토리지에서 즉시 복원(깜빡임 방지) */
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) setUser(JSON.parse(raw));
        } catch { }
    }, []);

    /* 2) 서버에서 세션 재검증(최초 mount 시 1회) */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(API_URL.AUTH_STATUS, { credentials: "include" });
                if (!res.ok) throw new Error("AUTH_STATUS failed");

                const data = await res.json();
                if (cancelled) return;

                if (data?.authenticated) {
                    const next = {
                        id: data.id,
                        tenant_id: data.tenant_id,
                        name: data.name,
                        user_key: data.user_key,
                        role: String(data.role ?? "USER").toUpperCase(),
                    };
                    setUser(next);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
                } else {
                    setUser(null);
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch (e) {
                if (cancelled) return;
                // ❗ 네트워크 오류 포함: 일단 세션은 없는 걸로 간주
                setUser(null);
                localStorage.removeItem(STORAGE_KEY);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /* 3) API 래퍼 */
    const perform_login = async (body) =>
        fetch(API_URL.LOGIN, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            credentials: "include",
            body,
        });

    const login = (id, tenant_id, name, user_key, role = "USER") => {
        const next = { id, tenant_id, name, user_key, role: String(role).toUpperCase() };
        setUser(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    const logout = async () => {
        try { await fetch(API_URL.LOGOUT, { method: "POST", credentials: "include" }); } catch { }
        setUser(null);
        localStorage.removeItem(STORAGE_KEY);
        // 리다이렉트는 호출자(레이아웃 버튼)에서 처리하거나 필요시 여기서 window.location 지정
    };

    const sign_up = async (body) =>
        fetch(API_URL.SIGNUP, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
        });

    const value = useMemo(
        () => (
            { user, isAuthenticated: !!user, login, logout, perform_login, sign_up, }
        ), [user]
    );

    if (loading) return null; // 필요시 스피너 UI

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* =========================
 *  Route Guards
 * ========================= */
export function RequireAuth({ children }) {
    const { isAuthenticated } = useAuth();
    const loc = useLocation();
    if (!isAuthenticated) {
        const from = loc.pathname + (loc.search || "") + (loc.hash || "");
        return <Navigate to="/login" state={{ from }} replace />;
    }
    return children;
}

export function RequireRole({ allow, children }) {
    const { user } = useAuth();
    const loc = useLocation();
    if (!user) {
        const from = loc.pathname + (loc.search || "") + (loc.hash || "");
        return <Navigate to="/login" state={{ from }} replace />;
    }

    const role = (user?.role || "USER").toUpperCase();
    const allowed = Array.isArray(allow)
        ? allow.map(String).map(s => s.toUpperCase()).includes(role)
        : String(allow || "").toUpperCase() === role;
    if (allowed) return children;

    const from = loc.pathname + (loc.search || "") + (loc.hash || "");
    return <Navigate to="/login" state={{ from }} replace />;
}
